import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useLocation, useSearchParams } from "react-router";
import { useAtomValue } from 'jotai';

import './navbar.css';
import { userAtom } from '../../../../state/userAtoms';

const RACES_ROOT = { label: 'Overview', to: '/races' } as const;

type UserNavLink = {
	label: string;
	to: string;
	isActive: (pathname: string) => boolean;
	/**
	 * When true, render a plain `<a href>` so the browser performs a full navigation.
	 * Use for URLs served outside this SPA (e.g. another app on the same origin).
	 */
	documentNavigation?: boolean;
};

/** Add entries here (optionally gated by `user.roles`) as new user-facing routes are added. */
const getUserNavLinks = (user: { name: string; roles: string[] }): UserNavLink[] => {
	if (!user.name) {
		return [];
	}
	const links: UserNavLink[] = [
		{
			label: 'My Times',
			to: '/account/myTimes',
			isActive: (p) => p.startsWith('/account/myTimes'),
		},
	];
	if (user.roles.includes('Scout') || user.roles.includes('Admin')) {
		links.push({
			label: 'Restream',
			to: '/restream',
			isActive: () => false,
			documentNavigation: true,
		});
	}
	return links;
};

const RACE_TYPE_NAV = [
	{ label: 'Blind Races', to: '/races?raceType=blindKaizo' },
	{ label: 'Mystery Monday', to: '/races?raceType=mysteryMonday' },
] as const;

export default function Navbar() {
	const { pathname } = useLocation();
	const [searchParams] = useSearchParams();
	const raceType = searchParams.get('raceType');
	const [isRacesOpen, setIsRacesOpen] = useState(false);
	const [isAccountOpen, setIsAccountOpen] = useState(false);
	const racesDropdownRef = useRef<HTMLDivElement>(null);
	const accountDropdownRef = useRef<HTMLDivElement>(null);
	const user = useAtomValue(userAtom);
	const isLoggedIn = user.isLoaded && Boolean(user.name);
	const userNavLinks = useMemo(() => getUserNavLinks(user), [user]);
	const isAccountNavActive = pathname.startsWith('/account');

	const isHome = pathname === '/';
	const isFaq = pathname.startsWith('/faq');
	const isRaces = pathname.startsWith('/races');

	const isDropdownItemActive = (to: string) => {
		if (to === '/races') return isRaces && !raceType;
		if (to.includes('raceType=blindKaizo')) return raceType === 'blindKaizo';
		if (to.includes('raceType=mysteryMonday')) return raceType === 'mysteryMonday';
		return false;
	};

	// Close mobile dropdowns when clicking outside
	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			const t = e.target as Node;
			if (racesDropdownRef.current && !racesDropdownRef.current.contains(t)) {
				setIsRacesOpen(false);
			}
			if (accountDropdownRef.current && !accountDropdownRef.current.contains(t)) {
				setIsAccountOpen(false);
			}
		};
		document.addEventListener('click', handleClickOutside);
		return () => document.removeEventListener('click', handleClickOutside);
	}, []);

	// Close when navigating (e.g. after tapping a sublink)
	useEffect(() => {
		setIsRacesOpen(false);
		setIsAccountOpen(false);
	}, [pathname]);

	return (
		<nav className="navbar">
			<Link to="/" className={`navLink ${isHome ? 'active' : ''}`}>
				Home
			</Link>
			<div
				ref={racesDropdownRef}
				className={`navDropdown ${isRacesOpen ? 'isOpen' : ''}`}
			>
				<Link
					to="/races"
					className={`navLink navLink--races ${isRaces ? 'active' : ''}`}
					aria-expanded={isRacesOpen}
					aria-haspopup="true"
				>
					Races
				</Link>
				<button
					type="button"
					className="navLink navLink--racesToggle"
					onClick={() => setIsRacesOpen(open => !open)}
					aria-expanded={isRacesOpen}
					aria-haspopup="true"
					aria-label="Toggle Races menu"
				>
					Races
					<span className="navDropdownChevron" aria-hidden>{isRacesOpen ? ' ▾' : ' ▸'}</span>
				</button>
				<div className="navDropdownPanel">
					<Link
						to={RACES_ROOT.to}
						className={`navDropdownLink navDropdownLink--overview ${isDropdownItemActive(RACES_ROOT.to) ? 'active' : ''}`}
						onClick={() => setIsRacesOpen(false)}
					>
						{RACES_ROOT.label}
					</Link>
					{RACE_TYPE_NAV.map(({ label, to }) => (
						<Link
							key={to}
							to={to}
							className={`navDropdownLink ${isDropdownItemActive(to) ? 'active' : ''}`}
							onClick={() => setIsRacesOpen(false)}
						>
							{label}
						</Link>
					))}
				</div>
			</div>
			<Link to="/faq" className={`navLink ${isFaq ? 'active' : ''}`}>
				FAQ
			</Link>
			{isLoggedIn && (
				<div
					ref={accountDropdownRef}
					className={`navDropdown navDropdown--account ${isAccountOpen ? 'isOpen' : ''}`}
				>
					<span
						className={`navLink navLink--accountLabel ${isAccountNavActive ? 'active' : ''}`}
						aria-haspopup="true"
					>
						Account
					</span>
					<button
						type="button"
						className={`navLink navLink--accountToggle ${isAccountNavActive ? 'active' : ''}`}
						onClick={() => setIsAccountOpen((open) => !open)}
						aria-expanded={isAccountOpen}
						aria-haspopup="true"
						aria-label="Toggle Account menu"
					>
						Account
						<span className="navDropdownChevron" aria-hidden>{isAccountOpen ? ' ▾' : ' ▸'}</span>
					</button>
					<div className="navDropdownPanel">
						{userNavLinks.map(
							({ label, to, isActive, documentNavigation }) => {
								const className = `navDropdownLink ${isActive(pathname) ? 'active' : ''}`;
								const close = () => setIsAccountOpen(false);
								if (documentNavigation) {
									return (
										<a key={to} href={to} className={className} onClick={close}>
											{label}
										</a>
									);
								}
								return (
									<Link key={to} to={to} className={className} onClick={close}>
										{label}
									</Link>
								);
							},
						)}
					</div>
				</div>
			)}
			<a
				href={isLoggedIn ? '/a/logout' : '/a/twitch/login'}
				className='navLink'
			>
				{isLoggedIn ? 'Logout' : 'Login'}
			</a>
		</nav>
	);
}
